import {
  computed,
  type MaybeRefOrGetter,
  onUnmounted,
  onWatcherCleanup,
  ref,
  toValue,
  watch,
  watchEffect,
} from "vue";

interface UseDropZoneOptions {
  accept?: MaybeRefOrGetter<string[] | undefined>;
  onDrop?: (files: File[] | null, event: DragEvent) => void;
  onEnter?: (files: File[] | null, event: DragEvent) => void;
  onLeave?: (files: File[] | null, event: DragEvent) => void;
  onOver?: (files: File[] | null, event: DragEvent) => void;
  multiple?: MaybeRefOrGetter<boolean>;
  preventDefaultForUnhandled?: boolean;
}

export const isAcceptedMimeType = (
  type: string,
  accepted: string[]
): boolean => {
  if (accepted.length === 0) {
    return true;
  }

  return accepted.some((acceptType) => {
    if (acceptType.startsWith(".")) {
      return false;
    }

    if (acceptType.endsWith("/*")) {
      return type.startsWith(acceptType.slice(0, -1));
    }

    return type === acceptType;
  });
};

/**
 * Check if a file is accepted based on both its MIME type and extension.
 * This is needed because browsers (especially Firefox) may report different
 * MIME types for the same file format. For example, CSV files in Firefox
 * may have MIME type "application/vnd.ms-excel" instead of "text/csv".
 */
export const isAcceptedFile = (file: File, accepted: string[]): boolean => {
  if (accepted.length === 0) {
    return true;
  }

  return accepted.some((acceptType) => {
    // Check file extension (e.g., ".csv")
    if (acceptType.startsWith(".")) {
      return file.name.toLowerCase().endsWith(acceptType.toLowerCase());
    }

    // Check wildcard MIME types (e.g., "image/*")
    if (acceptType.endsWith("/*")) {
      return file.type.startsWith(acceptType.slice(0, -1));
    }

    // Check exact MIME type match
    return file.type === acceptType;
  });
};

export const useDropzone = (
  target: MaybeRefOrGetter<HTMLElement | null>,
  {
    accept,
    onDrop,
    onEnter,
    onLeave,
    onOver,
    multiple = true,
    preventDefaultForUnhandled = false,
  }: UseDropZoneOptions = {}
) => {
  const isDropping = ref(false);

  const getFiles = (event: DragEvent) => {
    const list = Array.from(event.dataTransfer?.files ?? []);
    return list.length === 0 ? null : toValue(multiple) ? list : [list[0]];
  };

  const checkMimeTypes = (fileTypes: string[]): boolean => {
    const acceptedTypes = toValue(accept);
    if (!acceptedTypes) {
      return true;
    }

    // Filter to only MIME-type based accept values (not file extensions)
    // File extensions can only be checked on drop when we have the full File object
    const mimeOnlyAcceptTypes = acceptedTypes.filter((t) => !t.startsWith("."));

    // If only file extensions are specified, we can't validate during drag
    // so we allow the drop and validate the actual files later
    if (mimeOnlyAcceptTypes.length === 0) {
      return true;
    }

    return fileTypes.every((fileType) => {
      return isAcceptedMimeType(fileType, mimeOnlyAcceptTypes);
    });
  };

  const checkValidity = (event: DragEvent): boolean => {
    const mimes = Array.from(event.dataTransfer?.items ?? [], (f) => f.type);

    const typeValid = checkMimeTypes(mimes);
    const multipleFilesValid = toValue(multiple) || mimes.length <= 1;

    return typeValid && multipleFilesValid;
  };

  const filterAcceptedFiles = (files: File[] | null): File[] | null => {
    if (!files) return null;

    const acceptedTypes = toValue(accept);
    if (!acceptedTypes || acceptedTypes.length === 0) {
      return files;
    }

    const filtered = files.filter((file) =>
      isAcceptedFile(file, acceptedTypes)
    );
    return filtered.length > 0 ? filtered : null;
  };

  watchEffect(() => {
    const targetEl = toValue(target);
    if (!targetEl) {
      return;
    }

    let counter = 0;
    let isValid = true;

    const handleDragEvent = (
      type: "enter" | "over" | "leave" | "drop",
      event: DragEvent
    ) => {
      if (counter === 0) {
        isValid = checkValidity(event);
      }

      if (!isValid) {
        if (preventDefaultForUnhandled) {
          event.preventDefault();
        }
        if (event.dataTransfer) {
          event.dataTransfer.dropEffect = "none";
        }

        return;
      }

      event.preventDefault();
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = "copy";
      }

      const currentFiles = getFiles(event);

      if (type === "enter") {
        counter += 1;
        if (counter === 1) {
          isDropping.value = true;
        }

        onEnter?.(null, event);
      } else if (type === "over") {
        onOver?.(null, event);
      } else if (type === "leave") {
        counter -= 1;
        if (counter === 0) {
          isDropping.value = false;
        }

        onLeave?.(null, event);
      } else if (type === "drop") {
        counter = 0;
        isDropping.value = false;

        if (isValid) {
          // Filter files by accepted types (including file extensions)
          const acceptedFiles = filterAcceptedFiles(currentFiles);
          onDrop?.(acceptedFiles, event);
        }
      }
    };

    const controller = new AbortController();
    onWatcherCleanup(() => controller.abort());

    const options = { signal: controller.signal };

    targetEl.addEventListener(
      "dragenter",
      (ev) => handleDragEvent("enter", ev),
      options
    );
    targetEl.addEventListener(
      "dragover",
      (ev) => handleDragEvent("over", ev),
      options
    );
    targetEl.addEventListener(
      "dragleave",
      (ev) => handleDragEvent("leave", ev),
      options
    );
    targetEl.addEventListener(
      "drop",
      (ev) => handleDragEvent("drop", ev),
      options
    );
  });

  return { isDropping };
};

interface UseFilePickerOptions {
  /** @default true */
  multiple?: MaybeRefOrGetter<boolean>;
  /** @default [] */
  accept?: MaybeRefOrGetter<string[] | undefined>;
  onDrop: (files: null | File[]) => void;
}

export const useFilePicker = (props: UseFilePickerOptions) => {
  const acceptedTypes = computed(() => {
    return toValue(props.accept) ?? [];
  });

  const isMultiple = computed(() => {
    return toValue(props.multiple) ?? false;
  });

  const inputEl = document.createElement("input");
  inputEl.hidden = true;
  inputEl.type = "file";

  inputEl.onchange = () => {
    let files = Array.from(inputEl.files ?? []);
    inputEl.value = "";

    files = files.filter((file) => {
      return isAcceptedFile(file, acceptedTypes.value);
    });

    props.onDrop(files.length !== 0 ? files : null);
  };

  document.body.append(inputEl);

  watch(
    [acceptedTypes, isMultiple],
    ([accept, multiple]) => {
      inputEl.accept = accept.join(",");
      inputEl.multiple = multiple;
    },
    { immediate: true }
  );

  onUnmounted(() => {
    inputEl.remove();
  });

  return {
    open() {
      inputEl.click();
    },
  };
};
